<script setup lang="ts">
/**
 * A household's link, and the two ways to take it back (SPEC §10.9, §14.2).
 *
 * The two actions look like one and are not, so the copy does most of the work
 * here. An organizer reaching for this has usually just been told a link ended
 * up somewhere it should not, and the thing they most need to know is that
 * resetting it will not sign their family out of their phones.
 *
 * Both actions confirm before running. Neither is destructive in the sense of
 * losing data, and both are the sort of thing that is annoying to undo from the
 * other end of a text message.
 */
import { ref } from 'vue'
import { ApiError, endpoints, type HouseholdLinkResponse } from '@/api'

const props = defineProps<{
  groupId: string
  householdId: string
  householdName: string
}>()

const link = ref<HouseholdLinkResponse | null>(null)
const loading = ref(true)
const busy = ref<'reset' | 'sign-out' | null>(null)
const confirming = ref<'reset' | 'sign-out' | null>(null)
const notice = ref<string | null>(null)
const error = ref<string | null>(null)
const copied = ref(false)

/**
 * What to show when a call failed.
 *
 * A problem document is not guaranteed: a network failure or a proxy error
 * arrives with no body at all, and a message built from `problem.title` would
 * read "undefined" exactly when somebody is already having a bad time.
 */
function describe(caught: unknown): string {
  return (caught instanceof ApiError && caught.problem?.title) || 'That did not work.'
}

async function load() {
  loading.value = true
  error.value = null

  try {
    link.value = await endpoints.householdLink(props.groupId, props.householdId)
  } catch (caught) {
    error.value = describe(caught)
  } finally {
    loading.value = false
  }
}

async function reset() {
  confirming.value = null
  busy.value = 'reset'
  error.value = null
  notice.value = null

  try {
    link.value = await endpoints.resetHouseholdLink(props.groupId, props.householdId)
    notice.value = `${props.householdName} have a new link. The old one has stopped working.`
    copied.value = false
  } catch (caught) {
    error.value = describe(caught)
  } finally {
    busy.value = null
  }
}

async function signOut() {
  confirming.value = null
  busy.value = 'sign-out'
  error.value = null
  notice.value = null

  try {
    const result = await endpoints.revokeHouseholdSessions(props.groupId, props.householdId)

    // "Nobody was signed in" is a different thing to read than "that signed out
    // four devices", and an organizer who just pressed this needs to know which
    // happened.
    notice.value =
      result.endedSessions === 0
        ? 'Nobody was signed in on that household.'
        : result.endedSessions === 1
          ? 'Signed out 1 device. Their link still works.'
          : `Signed out ${result.endedSessions} devices. Their link still works.`
  } catch (caught) {
    error.value = describe(caught)
  } finally {
    busy.value = null
  }
}

async function copy() {
  if (!link.value) return

  try {
    await navigator.clipboard.writeText(link.value.url)
    copied.value = true
  } catch {
    // A browser that refuses clipboard access is not an error worth showing:
    // the link is on screen and selectable.
    copied.value = false
  }
}

void load()
</script>

<template>
  <v-card variant="outlined" class="access">
    <v-card-item>
      <v-card-title tag="h2" class="access__title">Family link</v-card-title>
      <v-card-subtitle class="access__subtitle">
        One link for the whole household. Anybody who has it can answer for {{ householdName }}.
      </v-card-subtitle>
    </v-card-item>

    <v-card-text>
      <v-alert v-if="error" type="error" variant="tonal" class="mb-4">{{ error }}</v-alert>
      <v-alert v-if="notice" type="success" variant="tonal" class="mb-4">{{ notice }}</v-alert>

      <v-skeleton-loader v-if="loading" type="text" />

      <div v-else-if="link" class="access__link">
        <code class="access__url" data-testid="household-link">{{ link.url }}</code>
        <v-btn variant="text" density="comfortable" @click="copy">
          {{ copied ? 'Copied' : 'Copy' }}
        </v-btn>
      </div>

      <v-divider class="my-6" />

      <section class="access__action">
        <h3 class="access__action-title">Reset the link</h3>
        <p class="access__action-body">
          Use this when the link has gone somewhere it should not — forwarded on, posted in a group
          chat, left on a shared computer. The old link stops working immediately and everybody in
          the household needs the new one.
          <strong>Nobody is signed out.</strong>
        </p>
        <v-btn
          variant="outlined"
          color="primary"
          :loading="busy === 'reset'"
          :disabled="busy !== null || loading"
          @click="confirming = 'reset'"
        >
          Reset family link
        </v-btn>
      </section>

      <section class="access__action">
        <h3 class="access__action-title">Sign out their devices</h3>
        <p class="access__action-body">
          Use this when a phone is lost or a device is no longer theirs. Everyone in the household
          is signed out and has to open the link again.
          <strong>The link itself keeps working.</strong>
        </p>
        <v-btn
          variant="outlined"
          :loading="busy === 'sign-out'"
          :disabled="busy !== null || loading"
          @click="confirming = 'sign-out'"
        >
          Sign out all devices
        </v-btn>
      </section>
    </v-card-text>

    <v-dialog
      :model-value="confirming !== null"
      max-width="30rem"
      @update:model-value="confirming = null"
    >
      <v-card v-if="confirming === 'reset'">
        <v-card-title tag="h2">Reset {{ householdName }}'s link?</v-card-title>
        <v-card-text>
          Their current link stops working straight away, and you will need to send them the new
          one. Their phones stay signed in.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="confirming = null">Keep it</v-btn>
          <v-btn variant="flat" color="primary" @click="reset">Reset the link</v-btn>
        </v-card-actions>
      </v-card>

      <v-card v-else-if="confirming === 'sign-out'">
        <v-card-title tag="h2">Sign out {{ householdName }}'s devices?</v-card-title>
        <v-card-text>
          Everyone in the household will need to open their link again. The link itself is
          unchanged, so you do not need to send a new one.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="confirming = null">Leave them</v-btn>
          <v-btn variant="flat" color="primary" @click="signOut">Sign them out</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<style scoped>
.access__title {
  font-size: 1.25rem;
  font-weight: 600;
}

.access__subtitle {
  white-space: normal;
  opacity: 1;
  color: rgb(var(--v-theme-on-surface));
}

.access__link {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

/* The link is 69 characters and has to survive a 360px screen without pushing
   the page sideways (SPEC §8.2). */
.access__url {
  flex: 1 1 16rem;
  min-width: 0;
  overflow-wrap: anywhere;
  font-size: 0.9375rem;
  line-height: 1.4;
}

.access__action + .access__action {
  margin-top: 2rem;
}

.access__action-title {
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 0.25rem;
}

.access__action-body {
  margin: 0 0 1rem;
  max-width: 44ch;
}
</style>
